import axios from "axios"
import { getToken } from "helpers/auth_helpers"
import { getErrorMessage } from "helpers/error_helpers"
import { crearParserSse } from "helpers/sse_parser"

const BASE_URL = `${window.REACT_AGRO_URL}/ewanagro/api/agro/migracion`

// BDSJEA-245: el fetch SSE no pasa por el interceptor de axios, así que el 403
// se traduce acá a un mensaje claro en lugar de "Error HTTP 403".
export const MENSAJE_SIN_PERMISO_IMPORTACION =
  "No tenés permiso para realizar la importación masiva."

export const listarCorridas = () =>
  axios.get(`${BASE_URL}/corridas`).then(res => res.data)

export const obtenerResultados = corrida =>
  axios
    .get(`${BASE_URL}/corridas/${encodeURIComponent(corrida)}/resultados`)
    .then(res => res.data)

// BDSJEA-341: estado (progreso, contadores, heartbeat) de la corrida en curso o
// de la última. El backend calcula el estado efectivo y los umbrales.
export const obtenerEstado = () =>
  axios.get(`${BASE_URL}/estado`).then(res => res.data)

export const obtenerEstadoCorrida = corrida =>
  axios
    .get(`${BASE_URL}/corridas/${encodeURIComponent(corrida)}/estado`)
    .then(res => res.data)

// El POST SSE usa fetch (no pasa por el interceptor de axios): el mensaje de
// error se arma con el mismo helper que el resto de la app, priorizando el
// `message` del cuerpo de la API. Nunca "Error HTTP NNN" ni texto crudo.
const leerCuerpoJson = async response => {
  try {
    return typeof response.json === "function" ? await response.json() : null
  } catch {
    return null
  }
}

// BDSJEA-353: códigos de error de la limpieza completa (contrato del backend).
export const CODIGO_LIMPIEZA_NO_DISPONIBLE = "LIMPIEZA_COMPLETA_NO_DISPONIBLE"
export const CODIGO_LIMPIEZA_CONFIRMACION_INVALIDA =
  "LIMPIEZA_CONFIRMACION_INVALIDA"

// BDSJEA-441: códigos de error de la importación por niveles (contrato del
// backend). El mensaje descriptivo lo trae el cuerpo (`message`); estos códigos
// solo identifican el caso. Nunca se reemplaza por un texto genérico.
export const CODIGO_NIVELES_INVALIDOS = "IMPORTACION_NIVELES_INVALIDOS"
export const CODIGO_PRERREQUISITO_FALTANTE =
  "IMPORTACION_PRERREQUISITO_FALTANTE"
export const CODIGO_MIGRADOR_SIN_SOPORTE_NIVELES =
  "MIGRADOR_SIN_SOPORTE_NIVELES"
// BDSJEA-454: el micro no admite recalcular el scoring (503 con mensaje fijo
// del backend, que se muestra tal cual).
export const CODIGO_MIGRADOR_SIN_SOPORTE_SCORING =
  "MIGRADOR_SIN_SOPORTE_SCORING"

export const mensajeErrorHttp = async response => {
  const data = await leerCuerpoJson(response)
  // El 403 genérico es "sin permiso"; el 403 de la limpieza trae su propio
  // mensaje (ambiente no habilitado) y se muestra tal cual.
  if (
    response.status === 403 &&
    data?.codigo !== CODIGO_LIMPIEZA_NO_DISPONIBLE
  ) {
    return MENSAJE_SIN_PERMISO_IMPORTACION
  }
  return getErrorMessage({ response: { status: response.status, data } })
}

// BDSJEA-353: ¿se puede ofrecer "Limpiar la base completa antes de importar"?
// Devuelve { disponible, ambiente, frasePedida, motivo }. La frase que hay que
// tipear la fija el backend (según el ambiente): el front no la hardcodea.
export const obtenerDisponibilidadLimpiezaCompleta = () =>
  axios
    .get(`${BASE_URL}/limpieza-completa/disponibilidad`)
    .then(res => res.data)
    .catch(err => {
      throw new Error(getErrorMessage(err))
    })

// BDSJEA-441: capacidades de la importación (GET /disponibilidad). `niveles`
// llega en true SOLO si el backend y el migrador soportan importar por niveles;
// en cualquier otro caso la propiedad no existe (fail-closed). BDSJEA-454:
// `scoring` (boolean) sigue la misma regla para «Recalcular scoring». Cualquier falla
// de la consulta se trata igual que la ausencia: sin selector.
// Timeout propio: una consulta colgada no deja el modal sin resolver (cae al
// camino fail-closed, igual que cualquier otra falla).
export const TIMEOUT_DISPONIBILIDAD_MS = 8000
export const obtenerDisponibilidad = () => {
  let temporizador
  const vencido = new Promise((_, reject) => {
    temporizador = setTimeout(
      () => reject(new Error("Timeout consultando /disponibilidad")),
      TIMEOUT_DISPONIBILIDAD_MS,
    )
  })
  const consulta = axios
    .get(`${BASE_URL}/disponibilidad`, { timeout: TIMEOUT_DISPONIBILIDAD_MS })
    .then(res => res.data)
  return Promise.race([consulta, vencido]).finally(() =>
    clearTimeout(temporizador),
  )
}

// BDSJEA-345: aviso (no error) cuando el stream cierra sin `done` ni `error`.
// Solo se usa de respaldo si quien llama no pasa `onSinDone`.
export const MENSAJE_STREAM_SIN_RESULTADO =
  "La salida en vivo se cerró sin informar el resultado final. Consultá el historial para ver el estado de la importación."

/**
 * Lanza la importación y hace streaming de la salida via SSE.
 * @param {object} params - { validarCuit: bool, detectarDuplicados: bool,
 *   limpiezaCompleta?: bool, confirmacionLimpieza?: string,
 *   excluirArca?: bool, recalcularScoring?: bool, niveles?: string }
 *   (BDSJEA-454: recalcularScoring solo viaja si es true; BDSJEA-353: los dos de limpieza
 *   solo viajan con limpiezaCompleta=true; excluirArca solo viaja si es true;
 *   BDSJEA-441: `niveles` es un CSV de ids canónicos y solo viaja si viene;
 *   sin ellos la llamada es idéntica a la anterior)
 * @param {function} onLog  - recibe cada linea de stdout del proceso
 * @param {function} onDone - recibe { exitCode, corrida } si el servidor mandó
 *   el evento `done` (nunca se fabrica un exitCode)
 * @param {function} onError - recibe el mensaje del evento `error` o del POST
 * @param {function} [onSinDone] - el stream terminó (o se cortó) sin `done` ni
 *   `error`: el resultado real hay que consultarlo en GET /estado
 * @returns {function} cancelar - llama para abortar el stream
 */
export const ejecutarMigracion = (
  params,
  onLog,
  onDone,
  onError,
  onSinDone,
) => {
  const {
    validarCuit = false,
    detectarDuplicados = false,
    limpiezaCompleta = false,
    confirmacionLimpieza = "",
    excluirArca = false,
    recalcularScoring = false,
    niveles = "",
  } = params
  const consulta = { validarCuit, detectarDuplicados }
  if (limpiezaCompleta === true) {
    consulta.limpiezaCompleta = true
    consulta.confirmacionLimpieza = confirmacionLimpieza
  }
  if (excluirArca === true) consulta.excluirArca = true
  if (recalcularScoring === true) consulta.recalcularScoring = true
  let query = new URLSearchParams(consulta).toString()
  if (typeof niveles === "string" && niveles.trim()) {
    // La coma separa los niveles: no se codifica (niveles=tambo,siembras).
    query += `&niveles=${encodeURIComponent(niveles.trim()).replace(/%2C/gi, ",")}`
  }
  const url = `${BASE_URL}/ejecutar?${query}`
  const token = getToken()
  const controller = new AbortController()

  const sinDone = () => {
    if (controller.signal.aborted) return
    if (onSinDone) onSinDone()
    else onError(MENSAJE_STREAM_SIN_RESULTADO)
  }

  const run = async () => {
    let streamAbierto = false
    try {
      const response = await fetch(url, {
        method: "POST",
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token?.accessToken}`,
          Accept: "text/event-stream",
        },
      })

      if (!response.ok) {
        onError(await mensajeErrorHttp(response))
        return
      }

      streamAbierto = true
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let recibioFin = false

      const parser = crearParserSse(({ evento, data }) => {
        if (evento === "log") {
          onLog(data)
        } else if (evento === "done") {
          recibioFin = true
          try {
            onDone(JSON.parse(data))
          } catch {
            onDone({ corrida: null })
          }
        } else if (evento === "error") {
          recibioFin = true // evitar doble reporte
          onError(data)
        }
      })

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        parser.push(decoder.decode(value, { stream: true }))
      }
      // Vacía el decoder y despacha el evento pendiente sin línea en blanco.
      parser.push(decoder.decode())
      parser.flush()

      if (!recibioFin) sinDone()
    } catch (err) {
      if (err.name === "AbortError" || controller.signal.aborted) return
      if (streamAbierto) {
        // El stream se cortó a mitad (proxy, timeout): el proceso puede seguir
        // vivo en el servidor; el estado real se consulta por /estado.
        sinDone()
        return
      }
      // Sin respuesta (red caída, servidor inalcanzable): texto en español
      // por tipo de error, no el "Failed to fetch" crudo del navegador.
      onError(getErrorMessage({ code: "ERR_NETWORK", message: err.message }))
    }
  }

  run()
  return () => controller.abort()
}
